// ─────────────────────────────────────────────────────────────────────────────
// Offering detail — mockup-styled view of a generated offering with
// edit-in-place.
//
// Clicking an offering card lands here: hero card (adgen image / placeholder,
// strength ring, meta chips), stats strip, tinted psychology cards, four-column
// insight lists, persona-fit strip with brand-persona avatars, then the rest of
// the generated data in consistent card sections. Every displayed field edits
// in place (click → input/select styled to match, partial PATCH on save) —
// there is no separate edit form. Duplicate clones the offering. Empty fields
// show a subtle "Add …" affordance so everything editable stays discoverable.
//
// PATCH discipline: only the edited field (or its whole jsonb section, rebuilt
// from the latest cache) is sent. Linkage fields (adgenImageRefs /
// includedProducts) are never included unless the user edits them — the
// server regenerates the AI hero when those change.
// ─────────────────────────────────────────────────────────────────────────────
import { useRef, useState, useCallback, useMemo } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, Loader2, Trash2, Tag, ImagePlus, Upload, X, Copy, Plus,
  Lightbulb, ShieldAlert, Shield, TrendingUp, Users, Sparkles, Target, Sprout,
  AlertTriangle, ChevronRight, Package, Truck, BadgeCheck, ShieldCheck,
  MousePointerClick, Paintbrush, Wrench, Brain, MessageSquareQuote, CircleDot,
  ExternalLink,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Offer, Persona, Product } from "@shared/schema";
import {
  computeOfferStrength, isOfferStrengthReady, offerStrengthCaption,
  type OfferStrengthInput,
} from "@shared/offer-strength";
import {
  OFFER_TYPES, PAIN_INTENSITIES, TIME_TO_VALUE, PRICING_MODELS,
  DELIVERY_MODELS, DELIVERY_LOCATIONS, PRIMARY_CTAS, SERVICE_TYPES, SERVICE_FORMATS,
} from "@shared/offer-template";
import { InlineText, InlineTextarea, InlineSelect, InlineStringList } from "@/components/inline-edit";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Shared option lists (same canonical values the create form uses).
const opts = (arr: readonly string[]) => arr.map((v) => ({ value: v, label: v.replace(/_/g, " ") }));
const OFFER_TYPE_OPTIONS = opts(OFFER_TYPES);
const PAIN_INTENSITY_OPTIONS = opts(PAIN_INTENSITIES);
const TIME_TO_VALUE_OPTIONS = opts(TIME_TO_VALUE);
const PRICING_MODEL_OPTIONS = opts(PRICING_MODELS);
const DELIVERY_MODEL_OPTIONS = opts(DELIVERY_MODELS);
const DELIVERY_LOCATION_OPTIONS = opts(DELIVERY_LOCATIONS);
const PRIMARY_CTA_OPTIONS = opts(PRIMARY_CTAS);
const SERVICE_TYPE_OPTIONS = opts(SERVICE_TYPES);
const SERVICE_FORMAT_OPTIONS = opts(SERVICE_FORMATS);
const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
];

// ---------------------------------------------------------------------------
// Small display helpers
// ---------------------------------------------------------------------------
const hasText = (v: any): v is string => typeof v === "string" && v.trim().length > 0;
const fmtEnum = (v: any) => (hasText(v) ? v.replace(/_/g, " ") : "");
const obj = (v: any): Record<string, any> => (v && typeof v === "object" && !Array.isArray(v) ? v : {});

/** Meaningful string items of a jsonb list (objects reduced via `pick`). */
function items(v: any, pick?: (e: any) => string | undefined): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((e) => (typeof e === "string" ? e : pick ? pick(e) : undefined))
    .filter((s): s is string => hasText(s))
    .map((s) => s.trim());
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";

type SavePatch = (patch: Record<string, any>) => Promise<any>;

// ---------------------------------------------------------------------------
// Building blocks (mockup card language)
// ---------------------------------------------------------------------------

function StrengthRing({ pct }: { pct: number }) {
  const C = 2 * Math.PI * 26; // r=26 in a 64x64 viewBox
  return (
    <div className="relative w-[68px] h-[68px] flex-shrink-0" data-testid="ring-offering-strength">
      <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
        <circle cx="32" cy="32" r="26" fill="none" strokeWidth="5" className="stroke-muted/70" />
        <circle
          cx="32" cy="32" r="26" fill="none" strokeWidth="5" strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * C} ${C}`}
          className={isOfferStrengthReady(pct) ? "stroke-emerald-500" : "stroke-emerald-600/70"}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums">
        {pct}%
      </span>
    </div>
  );
}

/** Meta chip whose value is an inline editor instead of static text. */
function EditableMetaChip({ label, children, testId }: { label: string; children: React.ReactNode; testId: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card px-3.5 py-2 min-w-[92px]" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">{label}</p>
      <div className="text-sm font-semibold capitalize leading-snug">{children}</div>
    </div>
  );
}

/** Tinted icon square + uppercase section title, matching persona-detail. */
function SectionHeader({ icon: Icon, title, accent, count }: { icon: any; title: string; accent: string; count?: number }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <h2 className="text-xs font-bold uppercase tracking-[0.12em]">{title}</h2>
      {count !== undefined && count > 0 && (
        <span className="text-[10px] font-semibold rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{count}</span>
      )}
    </div>
  );
}

function SectionCard({ children, testId, className = "" }: { children: React.ReactNode; testId?: string; className?: string }) {
  return (
    <div className={`rounded-2xl border border-border/60 bg-card shadow-sm p-5 ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

/** Labeled free-text block, edit-in-place. */
function EditableTextBlock({ label, value, onSave, testId }: { label: string; value: any; onSave: (v: string) => Promise<any>; testId?: string }) {
  const tid = testId ?? slugify(label);
  return (
    <div data-testid={tid}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 mb-1">{label}</p>
      <InlineTextarea
        value={hasText(value) ? value : ""}
        onSave={onSave}
        placeholder={`Add ${label.toLowerCase()}…`}
        className="text-sm leading-relaxed"
        testId={tid}
      />
    </div>
  );
}

/** Labeled list block (bullets / steps / chips), edit-in-place. */
function EditableListBlock({ label, list, onSave, variant = "bullets", testId }: {
  label: string;
  list: string[];
  onSave: (v: string[]) => Promise<any>;
  variant?: "bullets" | "steps" | "chips";
  testId?: string;
}) {
  const tid = testId ?? slugify(label);
  return (
    <div data-testid={tid}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 mb-1.5">{label}</p>
      <InlineStringList items={list} onSave={onSave} variant={variant} addLabel={`Add ${label.toLowerCase().replace(/s$/, "")}`} testId={tid} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Structured editors — packages/tiers and objection handling
// ---------------------------------------------------------------------------
function TiersEditor({ tiers, onSave }: { tiers: any[]; onSave: (next: any[]) => Promise<any> }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ tier_name: "", description: "", price_or_price_range: "", best_for: "" });
  const [saving, setSaving] = useState(false);

  const patchTier = (i: number, key: string, v: string) =>
    onSave(tiers.map((t, idx) => (idx === i ? { ...obj(t), [key]: v } : t)));
  const removeTier = async (i: number) => {
    setSaving(true);
    try { await onSave(tiers.filter((_, idx) => idx !== i)); } catch {} finally { setSaving(false); }
  };
  const commitAdd = async () => {
    if (!Object.values(draft).some(hasText)) { setAdding(false); return; }
    setSaving(true);
    try {
      await onSave([...tiers, { ...draft }]);
      setDraft({ tier_name: "", description: "", price_or_price_range: "", best_for: "" });
      setAdding(false);
    } catch {} finally { setSaving(false); }
  };

  return (
    <div className="mt-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 mb-2">Packages &amp; Tiers</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {tiers.map((t: any, i: number) => (
          <div key={i} className="group/tier relative rounded-xl border border-border/60 bg-muted/20 p-4 space-y-1.5" data-testid={`card-tier-${i}`}>
            <button
              type="button"
              onClick={() => removeTier(i)}
              disabled={saving}
              className="absolute top-2 right-2 rounded-full p-1 text-muted-foreground/50 hover:text-destructive opacity-0 group-hover/tier:opacity-100 transition-opacity"
              title="Remove tier"
              data-testid={`button-remove-tier-${i}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="flex items-start justify-between gap-2 pr-5">
              <InlineText value={t.tier_name} onSave={(v) => patchTier(i, "tier_name", v)} placeholder="Tier name…" className="text-sm font-semibold" testId={`tier-${i}-name`} />
              <span className="text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 px-2 py-0.5 whitespace-nowrap">
                <InlineText value={t.price_or_price_range} onSave={(v) => patchTier(i, "price_or_price_range", v)} placeholder="Price…" className="text-xs font-semibold" testId={`tier-${i}-price`} />
              </span>
            </div>
            <InlineTextarea value={t.description} onSave={(v) => patchTier(i, "description", v)} placeholder="Add description…" className="text-xs text-muted-foreground leading-snug" testId={`tier-${i}-description`} rows={2} />
            <div className="text-[11px] text-muted-foreground/80 flex gap-1">
              <span className="font-semibold shrink-0">Best for:</span>
              <InlineText value={t.best_for} onSave={(v) => patchTier(i, "best_for", v)} placeholder="Add…" className="text-[11px]" testId={`tier-${i}-best-for`} />
            </div>
          </div>
        ))}
        {adding ? (
          <div className="rounded-xl border border-dashed border-border p-4 space-y-2" data-testid="card-tier-new">
            <input autoFocus value={draft.tier_name} onChange={(e) => setDraft({ ...draft, tier_name: e.target.value })} placeholder="Tier name" className="w-full bg-background border border-input rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring" data-testid="input-new-tier-name" />
            <input value={draft.price_or_price_range} onChange={(e) => setDraft({ ...draft, price_or_price_range: e.target.value })} placeholder="Price or price range" className="w-full bg-background border border-input rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring" data-testid="input-new-tier-price" />
            <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Description" rows={2} className="w-full bg-background border border-input rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring resize-y" data-testid="input-new-tier-description" />
            <input value={draft.best_for} onChange={(e) => setDraft({ ...draft, best_for: e.target.value })} placeholder="Best for" className="w-full bg-background border border-input rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring" data-testid="input-new-tier-best-for" />
            <div className="flex gap-2 pt-1">
              <Button size="sm" className="h-7 text-xs" onClick={commitAdd} disabled={saving} data-testid="button-save-new-tier">
                {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Add tier
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAdding(false)} data-testid="button-cancel-new-tier">Cancel</Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="rounded-xl border border-dashed border-border p-4 flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground/60 hover:text-foreground hover:border-primary/40 transition-colors min-h-[88px]"
            data-testid="button-add-tier"
          >
            <Plus className="h-3.5 w-3.5" /> Add tier
          </button>
        )}
      </div>
    </div>
  );
}

function ObjectionsEditor({ objections, onSave }: { objections: any[]; onSave: (next: any[]) => Promise<any> }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ objection: "", response: "", proof_to_use: "" });
  const [saving, setSaving] = useState(false);

  // Normalize legacy plain-string entries into objects when edited.
  const norm = (e: any) => (typeof e === "string" ? { objection: e } : obj(e));

  const patchEntry = (i: number, key: string, v: string) =>
    onSave(objections.map((e, idx) => (idx === i ? { ...norm(e), [key]: v } : e)));
  const removeEntry = async (i: number) => {
    setSaving(true);
    try { await onSave(objections.filter((_, idx) => idx !== i)); } catch {} finally { setSaving(false); }
  };
  const commitAdd = async () => {
    if (!Object.values(draft).some(hasText)) { setAdding(false); return; }
    setSaving(true);
    try {
      await onSave([...objections, { ...draft }]);
      setDraft({ objection: "", response: "", proof_to_use: "" });
      setAdding(false);
    } catch {} finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {objections.map((raw: any, i: number) => {
        const e = norm(raw);
        return (
          <div key={i} className="group/obj relative rounded-xl border border-border/60 bg-muted/20 p-4 space-y-1.5" data-testid={`card-objection-${i}`}>
            <button
              type="button"
              onClick={() => removeEntry(i)}
              disabled={saving}
              className="absolute top-2 right-2 rounded-full p-1 text-muted-foreground/50 hover:text-destructive opacity-0 group-hover/obj:opacity-100 transition-opacity"
              title="Remove objection"
              data-testid={`button-remove-objection-${i}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="pr-5">
              <InlineTextarea value={e.objection} onSave={(v) => patchEntry(i, "objection", v)} placeholder="Add objection…" className="text-sm font-semibold leading-snug" testId={`objection-${i}-text`} rows={2} />
            </div>
            <InlineTextarea value={e.response} onSave={(v) => patchEntry(i, "response", v)} placeholder="Add response…" className="text-sm text-muted-foreground leading-snug" testId={`objection-${i}-response`} rows={2} />
            <div className="text-[11px] text-muted-foreground/80 flex gap-1">
              <span className="font-semibold shrink-0">Proof to use:</span>
              <InlineText value={e.proof_to_use} onSave={(v) => patchEntry(i, "proof_to_use", v)} placeholder="Add…" className="text-[11px]" testId={`objection-${i}-proof`} />
            </div>
          </div>
        );
      })}
      {adding ? (
        <div className="rounded-xl border border-dashed border-border p-4 space-y-2" data-testid="card-objection-new">
          <textarea autoFocus value={draft.objection} onChange={(e) => setDraft({ ...draft, objection: e.target.value })} placeholder='Objection (e.g. "It costs too much")' rows={2} className="w-full bg-background border border-input rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring resize-y" data-testid="input-new-objection-text" />
          <textarea value={draft.response} onChange={(e) => setDraft({ ...draft, response: e.target.value })} placeholder="Response" rows={2} className="w-full bg-background border border-input rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring resize-y" data-testid="input-new-objection-response" />
          <input value={draft.proof_to_use} onChange={(e) => setDraft({ ...draft, proof_to_use: e.target.value })} placeholder="Proof to use" className="w-full bg-background border border-input rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring" data-testid="input-new-objection-proof" />
          <div className="flex gap-2 pt-1">
            <Button size="sm" className="h-7 text-xs" onClick={commitAdd} disabled={saving} data-testid="button-save-new-objection">
              {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Add objection
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAdding(false)} data-testid="button-cancel-new-objection">Cancel</Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="rounded-xl border border-dashed border-border p-4 flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground/60 hover:text-foreground hover:border-primary/40 transition-colors min-h-[88px]"
          data-testid="button-add-objection"
        >
          <Plus className="h-3.5 w-3.5" /> Add objection
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adgen Image References (existing behavior, restyled wrapper)
// ---------------------------------------------------------------------------
function AdgenImageRefsSection({ offerId, brandId, currentRefs }: { offerId: string; brandId?: string; currentRefs: string[] }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const fileArr = Array.from(files).filter(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.name));
    if (fileArr.length === 0) {
      toast({ title: "Only image files (jpg, png, gif, webp) are allowed", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const uploadedUrls: string[] = [];
      for (const file of fileArr) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
        if (!res.ok) throw new Error("Upload failed");
        const data = await res.json();
        uploadedUrls.push(data.url || data.falUrl);
      }
      const updated = [...currentRefs, ...uploadedUrls];
      await apiRequest("PATCH", `/api/offers/${offerId}`, { adgenImageRefs: updated });
      queryClient.invalidateQueries({ queryKey: ["/api/offers", offerId] });
      if (brandId) queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/offers`] });
      toast({ title: `${uploadedUrls.length} image(s) added` });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }, [offerId, brandId, currentRefs, toast]);

  const removeImage = useCallback(async (url: string) => {
    const updated = currentRefs.filter(r => r !== url);
    try {
      await apiRequest("PATCH", `/api/offers/${offerId}`, { adgenImageRefs: updated });
      queryClient.invalidateQueries({ queryKey: ["/api/offers", offerId] });
      toast({ title: "Image removed" });
    } catch (e: any) {
      toast({ title: "Remove failed", description: e.message, variant: "destructive" });
    }
  }, [offerId, currentRefs, toast]);

  return (
    <SectionCard testId="section-adgen-refs">
      <div className="flex items-center justify-between">
        <SectionHeader
          icon={ImagePlus}
          title="Adgen Image References"
          count={currentRefs.length || undefined}
          accent="bg-sky-100 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300"
        />
        <Button
          variant="outline"
          size="sm"
          className="rounded-full -mt-4"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="button-upload-offer-adgen-refs"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
          {uploading ? "Uploading..." : "Upload Images"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              uploadFiles(e.target.files);
              e.target.value = "";
            }
          }}
        />
      </div>

      {currentRefs.length === 0 ? (
        <div
          className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${
            dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files); }}
          data-testid="dropzone-offer-adgen-refs"
        >
          <ImagePlus className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No adgen reference images yet</p>
          <p className="text-xs text-muted-foreground mt-1">Click or drag images here to add references for image generation</p>
        </div>
      ) : (
        <div
          className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 ${dragOver ? "ring-2 ring-primary/30 rounded-xl p-2" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files); }}
        >
          {currentRefs.map((url, i) => (
            <div key={`${url}-${i}`} className="relative group aspect-square rounded-xl border overflow-hidden bg-muted/30" data-testid={`offer-adgen-ref-${i}`}>
              <img src={url} alt={`Adgen ref ${i + 1}`} className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage(url)}
                className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                data-testid={`button-remove-offer-adgen-ref-${i}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/40 flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer"
            data-testid="button-add-more-offer-adgen-refs"
          >
            <ImagePlus className="h-5 w-5 text-muted-foreground/50" />
            <span className="text-[10px] text-muted-foreground">Add more</span>
          </button>
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Persona Fit strip — avatars matched to brand personas by name
// ---------------------------------------------------------------------------
function PersonaFitStrip({ fits, personas }: { fits: any[]; personas: Persona[] | undefined }) {
  const [expanded, setExpanded] = useState(false);

  const byName = useMemo(() => {
    const m = new Map<string, Persona>();
    for (const p of personas ?? []) m.set(p.name.trim().toLowerCase(), p);
    return m;
  }, [personas]);

  const entries = fits.map((f) => {
    const name = hasText(f?.persona_name) ? f.persona_name.trim() : "Unnamed persona";
    return { fit: f, name, persona: byName.get(name.toLowerCase()) };
  });

  const MAX_AVATARS = 6;
  const shown = entries.slice(0, MAX_AVATARS);
  const overflow = entries.length - shown.length;
  const highlighted = entries.slice(0, 3).map((e) => e.name);

  return (
    <SectionCard testId="section-persona-fit">
      <button
        type="button"
        className="w-full flex items-center gap-4 text-left"
        onClick={() => setExpanded((v) => !v)}
        data-testid="button-toggle-persona-fit"
      >
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
            <Users className="h-3.5 w-3.5" />
          </div>
          <span className="text-xs font-bold uppercase tracking-[0.12em]">Persona Fit</span>
        </div>

        <div className="flex items-center flex-shrink-0 pl-2">
          {shown.map((e, i) => (
            <div
              key={i}
              className="-ml-2 first:ml-0 h-10 w-10 rounded-full ring-2 ring-card overflow-hidden bg-muted flex items-center justify-center"
              title={e.name}
              data-testid={`avatar-persona-fit-${i}`}
            >
              {e.persona?.imageUrl ? (
                <img src={e.persona.imageUrl} alt={e.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-[11px] font-bold text-muted-foreground">{initials(e.name)}</span>
              )}
            </div>
          ))}
          {overflow > 0 && (
            <div className="-ml-2 h-10 w-10 rounded-full ring-2 ring-card bg-muted flex items-center justify-center text-[11px] font-bold text-muted-foreground" data-testid="avatar-persona-fit-overflow">
              +{overflow}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold" data-testid="text-persona-fit-headline">
            Strong fit for {entries.length} persona{entries.length === 1 ? "" : "s"}
          </p>
          {highlighted.length > 0 && (
            <p className="text-xs text-muted-foreground truncate">
              Especially aligned with {highlighted.join(", ")}.
            </p>
          )}
        </div>

        <ChevronRight className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="persona-fit-expanded">
          {entries.map((e, i) => (
            <div key={i} className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-2.5" data-testid={`card-persona-fit-${i}`}>
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-full overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
                  {e.persona?.imageUrl ? (
                    <img src={e.persona.imageUrl} alt={e.name} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-bold text-muted-foreground">{initials(e.name)}</span>
                  )}
                </div>
                <p className="text-sm font-semibold">{e.name}</p>
              </div>
              {hasText(e.fit?.why_they_need_it) && (
                <p className="text-sm text-muted-foreground leading-snug">{e.fit.why_they_need_it}</p>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {hasText(e.fit?.main_pain) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">Main Pain</p>
                    <p className="text-xs leading-snug">{e.fit.main_pain}</p>
                  </div>
                )}
                {hasText(e.fit?.main_desire) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">Main Desire</p>
                    <p className="text-xs leading-snug">{e.fit.main_desire}</p>
                  </div>
                )}
                {hasText(e.fit?.likely_objection) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">Likely Objection</p>
                    <p className="text-xs leading-snug">{e.fit.likely_objection}</p>
                  </div>
                )}
                {hasText(e.fit?.best_angle) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">Best Angle</p>
                    <p className="text-xs leading-snug">{e.fit.best_angle}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function OfferDetail() {
  const { brandId, offerId } = useParams<{ brandId: string; offerId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const { data: offer, isLoading } = useQuery<Offer>({
    queryKey: ["/api/offers", offerId],
    queryFn: async () => {
      const res = await fetch(`/api/offers/${offerId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load offering");
      return res.json();
    },
    enabled: !!offerId,
    // The product-based AI hero image lands in the background (same
    // lifecycle as the list page) — poll gently until it's ready or
    // terminally failed.
    refetchInterval: (query) => {
      const o = query.state.data as any;
      if (!o || o.heroImageUrl) return false;
      if (o.heroImageStatus === "generating") return 4000;
      if (o.heroImageStatus !== "failed") return 15000;
      return false;
    },
  });

  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });

  // Products back the same hero-photo fallback chain the offerings-list cards
  // use (offering reference images → first included product's photo), so the
  // strength score and hero imagery agree between the card and this page.
  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });

  // Field-level partial PATCH shared by every inline editor. The server
  // returns the updated row, which replaces the detail cache directly so
  // rapid successive per-field saves never clobber each other with stale
  // refetches; the list query is invalidated the normal way. Linkage fields
  // (adgenImageRefs / includedProducts) never ride along, so the AI hero is
  // never regenerated spuriously.
  const saveMutation = useMutation({
    mutationFn: async (patch: Record<string, any>) => {
      const res = await apiRequest("PATCH", `/api/offers/${offerId}`, patch);
      return res.json();
    },
    onSuccess: (updated: Offer) => {
      queryClient.setQueryData(["/api/offers", offerId], updated);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/offers`] });
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });
  const save: SavePatch = (patch) => saveMutation.mutateAsync(patch);
  // jsonb-section field save: rebuild the whole section object from the
  // LATEST cached row (not a render-time closure) so concurrent per-field
  // saves inside the same section can't clobber each other.
  const latestOffer = (): any => queryClient.getQueryData<Offer>(["/api/offers", offerId]) ?? offer;
  const saveSub = (field: string, patch: Record<string, any>) =>
    save({ [field]: { ...obj(latestOffer()?.[field]), ...patch } });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/offers/${offerId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/offers`] });
      toast({ title: "Offering deleted" });
      navigate(`/brands/${brandId}/offers`);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      if (!offer) throw new Error("Offering not loaded");
      const o: any = offer;
      const name = `${offer.name} (Copy)`;
      const body = {
        name,
        slug: slugify(name),
        status: offer.status,
        offerType: offer.offerType,
        offerCategory: o.offerCategory,
        summary: offer.summary,
        shortDescription: o.shortDescription,
        valueFraming: o.valueFraming,
        urgencyFraming: o.urgencyFraming,
        includedProducts: o.includedProducts,
        targetFunnelStage: o.targetFunnelStage,
        targetPersonas: o.targetPersonas,
        copyRules: o.copyRules,
        notes: o.notes,
        buyerProblem: o.buyerProblem,
        desiredOutcome: o.desiredOutcome,
        valueProposition: o.valueProposition,
        offerDetails: o.offerDetails,
        fulfillment: o.fulfillment,
        proof: o.proof,
        riskReversal: o.riskReversal,
        objections: o.objections,
        conversion: o.conversion,
        personaFit: o.personaFit,
        creativeInputs: o.creativeInputs,
        serviceDetails: o.serviceDetails,
        adgenImageRefs: o.adgenImageRefs,
      };
      const res = await apiRequest("POST", `/api/brands/${brandId}/offers`, body);
      return res.json();
    },
    onSuccess: (created: Offer) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/offers`] });
      toast({ title: "Offering duplicated" });
      navigate(`/brands/${brandId}/offers/${created.id}`);
    },
    onError: (e: Error) => toast({ title: "Duplicate failed", description: e.message, variant: "destructive" }),
  });

  if (isLoading) {
    return <div className="space-y-4 w-full px-8"><Skeleton className="h-10 w-48" /><Skeleton className="h-96" /></div>;
  }
  if (!offer) return <div className="p-6 text-muted-foreground">Offering not found</div>;

  const o: any = offer;
  const bp = obj(o.buyerProblem);
  const dOut = obj(o.desiredOutcome);
  const vp = obj(o.valueProposition);
  const od = obj(o.offerDetails);
  const ff = obj(o.fulfillment);
  const proof = obj(o.proof);
  const rr = obj(o.riskReversal);
  const cv = obj(o.conversion);
  const ci = obj(o.creativeInputs);
  const sd = obj(o.serviceDetails);

  const adgenRefs: string[] = Array.isArray(o.adgenImageRefs) ? o.adgenImageRefs.filter(hasText) : [];
  // Hero fallback chain — identical to the offerings-list card: own adgen
  // reference images, then the first included product's photo.
  let productPhoto: string | undefined;
  const includedIds = Array.isArray(o.includedProducts) ? (o.includedProducts as unknown[]) : [];
  for (const pid of includedIds) {
    const p = products?.find((x) => x.id === pid);
    if (!p) continue;
    const media = (p.mediaJson as unknown[] | null) || [];
    const productRefs = (p.adgenImageRefs as unknown[] | null) || [];
    const photo = [...media, ...productRefs].find((u): u is string => typeof u === "string" && !!u.trim());
    if (photo) { productPhoto = photo; break; }
  }
  const heroImage = adgenRefs[0] || productPhoto;
  // Image-panel chain: generated AI hero (product-based, lands in the
  // background) → real-photo chain → gradient placeholder. The strength
  // gauge keeps scoring the REAL-photo chain (same as the list card) so an
  // auto-generated hero doesn't inflate offering strength.
  const displayHero = (typeof o.heroImageUrl === "string" && o.heroImageUrl) || heroImage;

  // Same shared computation (and hero-photo input) as the offerings-list card
  // gauge — the two surfaces can never disagree on score or readiness.
  const strength = computeOfferStrength({ ...(o as OfferStrengthInput), hasHeroPhoto: !!heroImage });
  const ready = isOfferStrengthReady(strength);

  const coreBenefits = items(vp.core_benefits);
  const differentiators = items(vp.differentiators);
  const objections = Array.isArray(o.objections)
    ? o.objections.filter((e: any) => e && (typeof e === "string" ? hasText(e) : Object.values(obj(e)).some(hasText)))
    : [];
  const objectionTexts = items(o.objections, (e) => e?.objection);
  const trustSignals = items(proof.trust_signals);
  const personaFits = Array.isArray(o.personaFit)
    ? o.personaFit.filter((e: any) => e && (hasText(e.persona_name) || hasText(e.why_they_need_it)))
    : [];

  // Core Tension: the buyer's key hesitation — top objection, else status-quo cost.
  const coreTension = objectionTexts[0] || (hasText(bp.status_quo_cost) ? bp.status_quo_cost : "");

  const stats = [
    { label: "Core Benefits", count: coreBenefits.length, icon: Lightbulb, accent: "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300" },
    { label: "Objections", count: objections.length, icon: ShieldAlert, accent: "bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300" },
    { label: "Trust Signals", count: trustSignals.length, icon: Shield, accent: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300" },
    { label: "Differentiators", count: differentiators.length, icon: TrendingUp, accent: "bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300" },
    { label: "Persona Fits", count: personaFits.length, icon: Users, accent: "bg-sky-100 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300" },
  ];

  // Offering psychology cards. Three edit their underlying jsonb field in
  // place; Core Tension stays derived (top objection / status-quo cost).
  const psychCards: {
    key: string; label: string; icon: any; tint: string; head: string;
    text?: string; onSave?: (v: string) => Promise<any>; derivedHint?: string;
  }[] = [
    {
      key: "buyer-problem", label: "Buyer Problem", icon: Target,
      text: bp.primary_problem, onSave: (v) => saveSub("buyerProblem", { primary_problem: v }),
      tint: "bg-violet-50/70 border-violet-200/60 dark:bg-violet-950/25 dark:border-violet-900/40",
      head: "text-violet-600 dark:text-violet-300",
    },
    {
      key: "desired-outcome", label: "Desired Outcome", icon: Sprout,
      text: dOut.primary_outcome, onSave: (v) => saveSub("desiredOutcome", { primary_outcome: v }),
      tint: "bg-orange-50/70 border-orange-200/60 dark:bg-orange-950/25 dark:border-orange-900/40",
      head: "text-orange-600 dark:text-orange-300",
    },
    {
      key: "value-proposition", label: "Value Proposition", icon: Sparkles,
      text: vp.one_liner, onSave: (v) => saveSub("valueProposition", { one_liner: v }),
      tint: "bg-emerald-50/70 border-emerald-200/60 dark:bg-emerald-950/25 dark:border-emerald-900/40",
      head: "text-emerald-600 dark:text-emerald-300",
    },
    {
      key: "core-tension", label: "Core Tension", icon: AlertTriangle,
      text: coreTension, derivedHint: "Derived from the top objection or status-quo cost",
      tint: "bg-amber-50/70 border-amber-200/60 dark:bg-amber-950/25 dark:border-amber-900/40",
      head: "text-amber-600 dark:text-amber-300",
    },
  ];

  // Four-column insight lists. Three edit in place; the Objections column is
  // a read-only digest of the structured entries managed in Objection
  // Handling below.
  const insightColumns: {
    key: string; title: string; icon: any; list: string[]; unit: string; accent: string;
    onSave?: (v: string[]) => Promise<any>; readOnlyHint?: string;
  }[] = [
    {
      key: "core-benefits", title: "Core Benefits", icon: Lightbulb, list: coreBenefits, unit: "benefits",
      accent: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
      onSave: (v) => saveSub("valueProposition", { core_benefits: v }),
    },
    {
      key: "objections", title: "Objections", icon: ShieldAlert, list: objectionTexts, unit: "objections",
      accent: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
      readOnlyHint: "Managed in Objection Handling below",
    },
    {
      key: "trust-signals", title: "Trust Signals", icon: Shield, list: trustSignals, unit: "signals",
      accent: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
      onSave: (v) => saveSub("proof", { trust_signals: v }),
    },
    {
      key: "differentiators", title: "Differentiators", icon: TrendingUp, list: differentiators, unit: "differentiators",
      accent: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
      onSave: (v) => saveSub("valueProposition", { differentiators: v }),
    },
  ];

  const tiers: any[] = Array.isArray(od.packages_or_tiers)
    ? od.packages_or_tiers.filter((t: any) => t && typeof t === "object")
    : [];

  // Service Details only applies to service offerings — keep it visible for
  // those (or when legacy data exists) instead of adding noise to bundles.
  const showService =
    offer.offerType === "service"
    || hasText(sd.service_scope) || items(sd.deliverables).length > 0 || items(sd.client_inputs_needed).length > 0
    || items(sd.provider_actions).length > 0 || items(sd.client_actions).length > 0
    || hasText(sd.success_definition) || hasText(sd.service_area) || hasText(sd.capacity_limits)
    || items(sd.booking_or_sales_process).length > 0 || hasText(sd.service_type) || hasText(sd.service_format);

  return (
    <div className="w-full px-8 pb-10">
      <div className="sticky top-0 z-10 -mx-8 px-8 py-2 mb-4 bg-background/80 backdrop-blur border-b border-border/40 flex items-center justify-between gap-3">
        <Link href={`/brands/${brandId}/offers`}>
          <Button variant="ghost" size="sm" className="h-9 -ml-2 text-muted-foreground hover:text-foreground" data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Offerings
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          {saveMutation.isPending && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="indicator-saving">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
            </span>
          )}
          <Button variant="outline" size="sm" className="h-9" onClick={() => duplicateMutation.mutate()} disabled={duplicateMutation.isPending} data-testid="button-duplicate-offer">
            {duplicateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
            Duplicate
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/5" data-testid="button-delete-offer">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this offering?</AlertDialogTitle>
                <AlertDialogDescription>This permanently removes the offering. Strategy nodes referencing it will lose the link.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="button-confirm-delete" onClick={() => deleteMutation.mutate()}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="space-y-4">
        {/* ── Hero ──────────────────────────────────────────────────── */}
        <SectionCard testId="offer-hero" className="p-5 md:p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="relative w-full lg:w-[380px] xl:w-[440px] flex-shrink-0 aspect-[4/3] rounded-2xl overflow-hidden border border-border/50 bg-muted/40">
              {displayHero ? (
                <img src={displayHero} alt={offer.name} className="absolute inset-0 w-full h-full object-cover" data-testid="img-offer-hero" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-amber-100/70 via-muted/60 to-emerald-100/50 dark:from-amber-900/20 dark:via-muted/40 dark:to-emerald-900/15 flex items-center justify-center" data-testid="placeholder-offer-hero">
                  <Tag className="h-14 w-14 text-muted-foreground/25" />
                </div>
              )}
              <div className="absolute top-3 left-3 flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em]">
                  <CircleDot className="h-3 w-3 text-amber-500" /> Offering
                </span>
                {hasText(offer.offerType) && (
                  <span className="rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[10px] font-semibold capitalize">
                    {fmtEnum(offer.offerType)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0 space-y-4">
              <h1 className="text-2xl md:text-3xl font-bold leading-tight">
                <InlineText
                  value={offer.name}
                  onSave={(v) => { if (v) return save({ name: v }); }}
                  placeholder="Offering name"
                  testId="offer-name"
                />
              </h1>

              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  ready
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                }`}
                data-testid="pill-offer-ready"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${ready ? "bg-emerald-500" : "bg-amber-500"}`} />
                {ready ? "Offer Ready" : "In Progress"}
              </span>

              <div className="flex items-center gap-3">
                <StrengthRing pct={strength} />
                <div>
                  <p className="text-sm font-semibold">Offering Strength</p>
                  <p className="text-xs text-muted-foreground" data-testid="text-strength-caption">{offerStrengthCaption(strength)}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <EditableMetaChip label="Type" testId="chip-meta-type">
                  <InlineSelect value={offer.offerType} options={OFFER_TYPE_OPTIONS} onSave={(v) => save({ offerType: v })} className="capitalize" testId="offer-type" />
                </EditableMetaChip>
                <EditableMetaChip label="Category" testId="chip-meta-category">
                  <InlineText value={o.offerCategory} onSave={(v) => save({ offerCategory: v || null })} placeholder="Add…" testId="offer-category" />
                </EditableMetaChip>
                <EditableMetaChip label="Price Model" testId="chip-meta-pricing">
                  <InlineSelect value={od.pricing_model} options={PRICING_MODEL_OPTIONS} onSave={(v) => saveSub("offerDetails", { pricing_model: v })} className="capitalize" testId="offer-pricing-model" />
                </EditableMetaChip>
                <EditableMetaChip label="Status" testId="chip-meta-status">
                  <InlineSelect value={offer.status} options={STATUS_OPTIONS} onSave={(v) => save({ status: v })} className="capitalize" testId="offer-status" />
                </EditableMetaChip>
              </div>

              <InlineTextarea
                value={o.shortDescription}
                onSave={(v) => save({ shortDescription: v || null })}
                placeholder="Add short description…"
                className="text-sm text-muted-foreground leading-relaxed"
                testId="offer-description"
              />
              <InlineTextarea
                value={offer.summary}
                onSave={(v) => save({ summary: v || null })}
                placeholder="Add summary…"
                className="text-sm text-muted-foreground/80 leading-relaxed"
                testId="offer-summary"
              />
            </div>
          </div>
        </SectionCard>

        {/* ── Stats strip ───────────────────────────────────────────── */}
        {stats.some((s) => s.count > 0) && (
          <SectionCard testId="offer-stats-strip" className="py-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-3 divide-x-0 lg:divide-x divide-border/50">
              {stats.map((s) => (
                <div key={s.label} className="flex items-center gap-3 px-4 first:pl-1" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className={`h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 ${s.accent}`}>
                    <s.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-base font-bold leading-none tabular-nums">{s.count}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* ── Offering psychology ───────────────────────────────────── */}
        <SectionCard testId="section-psychology">
          <SectionHeader icon={Brain} title="Offering Psychology" accent="bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {psychCards.map((c) => (
              <div key={c.key} className={`rounded-xl border p-4 ${c.tint}`} data-testid={`card-psych-${c.key}`}>
                <div className={`flex items-center gap-1.5 mb-2 ${c.head}`}>
                  <c.icon className="h-3.5 w-3.5" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.1em]">{c.label}</span>
                </div>
                {c.onSave ? (
                  <InlineTextarea
                    value={c.text}
                    onSave={c.onSave}
                    placeholder={`Add ${c.label.toLowerCase()}…`}
                    className="text-sm leading-relaxed"
                    testId={`psych-${c.key}`}
                  />
                ) : (
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {hasText(c.text) ? c.text : <span className="italic text-muted-foreground/50">{c.derivedHint}</span>}
                  </p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── Four-column insight lists ─────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="offer-insight-grid">
          {insightColumns.map((c) => (
            <SectionCard key={c.key} testId={`card-${c.key}`} className="flex flex-col">
              <SectionHeader icon={c.icon} title={c.title} accent={c.accent} />
              <div className="flex-1">
                {c.onSave ? (
                  <InlineStringList items={c.list} onSave={c.onSave} variant="bullets" addLabel={`Add ${c.unit.replace(/s$/, "")}`} testId={c.key} />
                ) : c.list.length > 0 ? (
                  <ul className="space-y-2.5">
                    {c.list.map((s, i) => (
                      <li key={i} className="flex gap-2 text-sm leading-snug">
                        <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-muted-foreground/40 flex-shrink-0" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm italic text-muted-foreground/50">{c.readOnlyHint}</p>
                )}
              </div>
              <span className="mt-4 self-start text-[11px] font-semibold rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                {c.list.length} total {c.unit}
              </span>
            </SectionCard>
          ))}
        </div>

        {/* ── Persona fit strip ─────────────────────────────────────── */}
        {personaFits.length > 0 && <PersonaFitStrip fits={personaFits} personas={personas} />}

        {/* ── Problem & outcome detail ──────────────────────────────── */}
        <SectionCard testId="section-problem-outcome">
          <SectionHeader icon={Target} title="Problem & Outcome" accent="bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300" />
          <div className="flex flex-wrap gap-2 mb-4">
            <EditableMetaChip label="Pain Intensity" testId="chip-pain-intensity">
              <InlineSelect value={bp.pain_intensity} options={PAIN_INTENSITY_OPTIONS} onSave={(v) => saveSub("buyerProblem", { pain_intensity: v })} className="capitalize" testId="pain-intensity" />
            </EditableMetaChip>
            <EditableMetaChip label="Time to Value" testId="chip-time-to-value">
              <InlineSelect value={dOut.time_to_value} options={TIME_TO_VALUE_OPTIONS} onSave={(v) => saveSub("desiredOutcome", { time_to_value: v })} className="capitalize" testId="time-to-value" />
            </EditableMetaChip>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
            <EditableListBlock label="Secondary Problems" list={items(bp.secondary_problems)} onSave={(v) => saveSub("buyerProblem", { secondary_problems: v })} />
            <EditableListBlock label="Current Alternatives" list={items(bp.current_alternatives)} onSave={(v) => saveSub("buyerProblem", { current_alternatives: v })} />
            <EditableTextBlock label="Status-Quo Cost" value={bp.status_quo_cost} onSave={(v) => saveSub("buyerProblem", { status_quo_cost: v })} />
            <EditableListBlock label="Secondary Outcomes" list={items(dOut.secondary_outcomes)} onSave={(v) => saveSub("desiredOutcome", { secondary_outcomes: v })} />
            <EditableTextBlock label="Emotional Outcome" value={dOut.emotional_outcome} onSave={(v) => saveSub("desiredOutcome", { emotional_outcome: v })} />
            <EditableTextBlock label="Business / Life Impact" value={dOut.business_or_life_impact} onSave={(v) => saveSub("desiredOutcome", { business_or_life_impact: v })} />
          </div>
        </SectionCard>

        {/* ── Offer details ─────────────────────────────────────────── */}
        <SectionCard testId="section-offer-details">
          <SectionHeader icon={Package} title="Offer Details" accent="bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300" count={tiers.length || undefined} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mb-1">
            <EditableListBlock label="What's Included" list={items(od.what_is_included)} onSave={(v) => saveSub("offerDetails", { what_is_included: v })} testId="whats-included" />
            <EditableListBlock label="What's Not Included" list={items(od.what_is_not_included)} onSave={(v) => saveSub("offerDetails", { what_is_not_included: v })} testId="whats-not-included" />
            <EditableTextBlock label="Availability" value={od.availability} onSave={(v) => saveSub("offerDetails", { availability: v })} />
            <EditableTextBlock label="Why Now" value={vp.why_now} onSave={(v) => saveSub("valueProposition", { why_now: v })} />
            <EditableTextBlock label="Why This Brand" value={vp.why_this_brand} onSave={(v) => saveSub("valueProposition", { why_this_brand: v })} />
          </div>
          <TiersEditor tiers={tiers} onSave={(next) => saveSub("offerDetails", { packages_or_tiers: next })} />
        </SectionCard>

        {/* ── Fulfillment & process ─────────────────────────────────── */}
        <SectionCard testId="section-fulfillment">
          <SectionHeader icon={Truck} title="Fulfillment & Process" accent="bg-sky-100 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300" />
          <div className="flex flex-wrap gap-2 mb-4">
            <EditableMetaChip label="Delivery Model" testId="chip-delivery-model">
              <InlineSelect value={ff.delivery_model} options={DELIVERY_MODEL_OPTIONS} onSave={(v) => saveSub("fulfillment", { delivery_model: v })} className="capitalize" testId="delivery-model" />
            </EditableMetaChip>
            <EditableMetaChip label="Delivery Location" testId="chip-delivery-location">
              <InlineSelect value={ff.delivery_location} options={DELIVERY_LOCATION_OPTIONS} onSave={(v) => saveSub("fulfillment", { delivery_location: v })} className="capitalize" testId="delivery-location" />
            </EditableMetaChip>
            <EditableMetaChip label="Timeline" testId="chip-timeline">
              <InlineText value={ff.timeline} onSave={(v) => saveSub("fulfillment", { timeline: v })} placeholder="Add…" testId="timeline" />
            </EditableMetaChip>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
            <EditableListBlock label="Process Steps" list={items(ff.process_steps)} onSave={(v) => saveSub("fulfillment", { process_steps: v })} variant="steps" />
            <EditableListBlock label="Customer Requirements" list={items(ff.customer_requirements)} onSave={(v) => saveSub("fulfillment", { customer_requirements: v })} />
            <EditableListBlock label="Onboarding Steps" list={items(ff.onboarding_steps)} onSave={(v) => saveSub("fulfillment", { onboarding_steps: v })} variant="steps" />
          </div>
        </SectionCard>

        {/* ── Proof ─────────────────────────────────────────────────── */}
        <SectionCard testId="section-proof">
          <SectionHeader icon={BadgeCheck} title="Proof" accent="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
            <EditableListBlock label="Case Studies" list={items(proof.case_studies)} onSave={(v) => saveSub("proof", { case_studies: v })} />
            <EditableListBlock label="Testimonials" list={items(proof.testimonials)} onSave={(v) => saveSub("proof", { testimonials: v })} />
            <EditableListBlock label="Metrics" list={items(proof.metrics)} onSave={(v) => saveSub("proof", { metrics: v })} />
            <EditableListBlock label="Credentials" list={items(proof.credentials)} onSave={(v) => saveSub("proof", { credentials: v })} />
            <EditableListBlock label="Before / After Examples" list={items(proof.before_after_examples)} onSave={(v) => saveSub("proof", { before_after_examples: v })} testId="before-after-examples" />
          </div>
        </SectionCard>

        {/* ── Risk reversal ─────────────────────────────────────────── */}
        <SectionCard testId="section-risk-reversal">
          <SectionHeader icon={ShieldCheck} title="Risk Reversal & Guarantees" accent="bg-teal-100 text-teal-600 dark:bg-teal-900/40 dark:text-teal-300" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <EditableTextBlock label="Guarantee" value={rr.guarantee} onSave={(v) => saveSub("riskReversal", { guarantee: v })} />
            <EditableTextBlock label="Refund Policy" value={rr.refund_policy} onSave={(v) => saveSub("riskReversal", { refund_policy: v })} />
            <EditableTextBlock label="Contract Terms" value={rr.contract_terms} onSave={(v) => saveSub("riskReversal", { contract_terms: v })} />
            <EditableTextBlock label="Trial / Intro Offer" value={rr.trial_or_intro_offer} onSave={(v) => saveSub("riskReversal", { trial_or_intro_offer: v })} testId="trial-intro-offer" />
          </div>
        </SectionCard>

        {/* ── Objection handling (add / edit / remove entries) ──────── */}
        <SectionCard testId="section-objection-handling">
          <SectionHeader icon={MessageSquareQuote} title="Objection Handling" accent="bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300" count={objections.length || undefined} />
          <ObjectionsEditor objections={objections} onSave={(next) => save({ objections: next })} />
        </SectionCard>

        {/* ── Conversion ────────────────────────────────────────────── */}
        <SectionCard testId="section-conversion">
          <SectionHeader icon={MousePointerClick} title="Conversion" accent="bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300" />
          <div className="flex flex-wrap gap-2 mb-4">
            <EditableMetaChip label="Primary CTA" testId="chip-primary-cta">
              <InlineSelect value={cv.primary_cta} options={PRIMARY_CTA_OPTIONS} onSave={(v) => saveSub("conversion", { primary_cta: v })} className="capitalize" testId="primary-cta" />
            </EditableMetaChip>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <EditableTextBlock label="CTA Copy" value={cv.cta_copy} onSave={(v) => saveSub("conversion", { cta_copy: v })} />
            <div data-testid="text-conversion-url">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 mb-1 flex items-center gap-1.5">
                Conversion URL
                {hasText(cv.conversion_url) && (
                  <a href={cv.conversion_url} target="_blank" rel="noopener noreferrer" className="text-primary" title="Open link" data-testid="link-conversion-url">
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </p>
              <InlineText
                value={cv.conversion_url}
                onSave={(v) => saveSub("conversion", { conversion_url: v })}
                placeholder="Add conversion URL…"
                className="text-sm text-primary underline underline-offset-2 break-all"
                testId="conversion-url"
              />
            </div>
            <EditableListBlock label="Lead-Capture Fields" list={items(cv.lead_capture_fields)} onSave={(v) => saveSub("conversion", { lead_capture_fields: v })} variant="chips" />
            <EditableListBlock label="Qualification Criteria" list={items(cv.qualification_criteria)} onSave={(v) => saveSub("conversion", { qualification_criteria: v })} />
          </div>
        </SectionCard>

        {/* ── Creative inputs ───────────────────────────────────────── */}
        <SectionCard testId="section-creative-inputs">
          <SectionHeader icon={Paintbrush} title="Creative Inputs" accent="bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-900/40 dark:text-fuchsia-300" />
          <div className="mb-4">
            <EditableTextBlock label="Tone" value={ci.tone} onSave={(v) => saveSub("creativeInputs", { tone: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
            <EditableListBlock label="Hooks" list={items(ci.hooks)} onSave={(v) => saveSub("creativeInputs", { hooks: v })} />
            <EditableListBlock label="Angles" list={items(ci.angles)} onSave={(v) => saveSub("creativeInputs", { angles: v })} />
            <EditableListBlock label="Claims" list={items(ci.claims)} onSave={(v) => saveSub("creativeInputs", { claims: v })} />
            <EditableListBlock label="Forbidden Claims" list={items(ci.forbidden_claims)} onSave={(v) => saveSub("creativeInputs", { forbidden_claims: v })} />
            <EditableListBlock label="Visual Direction" list={items(ci.visual_direction)} onSave={(v) => saveSub("creativeInputs", { visual_direction: v })} />
            <EditableListBlock label="UGC Scenarios" list={items(ci.ugc_scenarios)} onSave={(v) => saveSub("creativeInputs", { ugc_scenarios: v })} />
            <EditableListBlock label="Landing-Page Messages" list={items(ci.landing_page_messages)} onSave={(v) => saveSub("creativeInputs", { landing_page_messages: v })} />
          </div>
        </SectionCard>

        {/* ── Service details ───────────────────────────────────────── */}
        {showService && (
          <SectionCard testId="section-service-details">
            <SectionHeader icon={Wrench} title="Service Details" accent="bg-slate-200 text-slate-600 dark:bg-slate-800/70 dark:text-slate-300" />
            <div className="flex flex-wrap gap-2 mb-4">
              <EditableMetaChip label="Service Type" testId="chip-service-type">
                <InlineSelect value={sd.service_type} options={SERVICE_TYPE_OPTIONS} onSave={(v) => saveSub("serviceDetails", { service_type: v })} className="capitalize" testId="service-type" />
              </EditableMetaChip>
              <EditableMetaChip label="Service Format" testId="chip-service-format">
                <InlineSelect value={sd.service_format} options={SERVICE_FORMAT_OPTIONS} onSave={(v) => saveSub("serviceDetails", { service_format: v })} className="capitalize" testId="service-format" />
              </EditableMetaChip>
              <EditableMetaChip label="Service Area" testId="chip-service-area">
                <InlineText value={sd.service_area} onSave={(v) => saveSub("serviceDetails", { service_area: v })} placeholder="Add…" testId="service-area" />
              </EditableMetaChip>
              <EditableMetaChip label="Capacity" testId="chip-service-capacity">
                <InlineText value={sd.capacity_limits} onSave={(v) => saveSub("serviceDetails", { capacity_limits: v })} placeholder="Add…" testId="service-capacity" />
              </EditableMetaChip>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
              <EditableTextBlock label="Service Scope" value={sd.service_scope} onSave={(v) => saveSub("serviceDetails", { service_scope: v })} />
              <EditableListBlock label="Deliverables" list={items(sd.deliverables)} onSave={(v) => saveSub("serviceDetails", { deliverables: v })} />
              <EditableListBlock label="Client Inputs Needed" list={items(sd.client_inputs_needed)} onSave={(v) => saveSub("serviceDetails", { client_inputs_needed: v })} />
              <EditableListBlock label="Provider Actions" list={items(sd.provider_actions)} onSave={(v) => saveSub("serviceDetails", { provider_actions: v })} />
              <EditableListBlock label="Client Actions" list={items(sd.client_actions)} onSave={(v) => saveSub("serviceDetails", { client_actions: v })} />
              <EditableTextBlock label="Success Definition" value={sd.success_definition} onSave={(v) => saveSub("serviceDetails", { success_definition: v })} />
              <EditableListBlock label="Booking / Sales Process" list={items(sd.booking_or_sales_process)} onSave={(v) => saveSub("serviceDetails", { booking_or_sales_process: v })} variant="steps" testId="booking-sales-process" />
            </div>
          </SectionCard>
        )}
      </div>

      {/* ── Adgen image references (always last) ───────────────────────── */}
      <div className="mt-4">
        <AdgenImageRefsSection
          offerId={offer.id}
          brandId={brandId}
          currentRefs={adgenRefs}
        />
      </div>
    </div>
  );
}
